import React, {useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Pressable, View} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {zodResolver} from '@hookform/resolvers/zod';
import {observer} from 'mobx-react-lite';

import {VerificationFormData, verificationSchema} from '../../screens/account/schemas/authSchemas';
import BackButton from '../rewards/BackButton';
import RewardsProgramImage from '../rewards/ProgramImage';
import Button from '../themed/Button';
import Text from '../themed/Text';
import ValidationMessage from '../themed/ValidationMessage';
import Otp from '../ui-controls/form/Otp';
import ContentView from '../ui-controls/layout/ContentView';

/**
 * Timeout for sending the code
 */
const TIMEOUT = 30000;

/**
 * Disable the resend link until this much time
 */
const RESEND_TIMEOUT = 10000;

/**
 * Wait for [delay] ms if [promise] returns first.
 * @param promise
 * @param delay
 * @returns
 */
async function slowDown<T>(promise: Promise<T>, delay: number = RESEND_TIMEOUT): Promise<T> {
  const [value] = await Promise.all([
    promise,
    new Promise(resolve => {
      setTimeout(resolve, delay);
    }),
  ]);

  return value;
}

type Props = {
  /**
   * This is used to show the user the number the sms was sent to
   */
  phoneNumber?: string;

  /**
   * This is used to show the user the email the email was sent to
   */
  emailAddress?: string;

  /**
   * handler when verify button cta is pressed
   * @param values
   * @param helpers
   * @returns
   */
  onSubmit: (
    values: VerificationFormData,
    helpers: {
      setError: (field: 'verification', error: {type: string; message: string}) => void;
    },
  ) => Promise<void>;

  /**
   * Handler to resend the verification code.
   * Should return true if success false if failed.
   * @returns
   */
  onResend: () => Promise<boolean>;

  /**
   * Handle back button press
   * @returns
   */
  onBack: () => void;

  /**
   * Type of verification
   */
  type: 'email' | 'phone';
};

/**
 * This screen component is used in sign up flows where phone number verification is needed.
 */
const PhoneOrEmailVerification = observer(
  ({emailAddress, phoneNumber, onSubmit: _onSubmit, onResend: _onResend, onBack, type}: Props) => {
    const [resending, setResending] = useState(false);
    const [codeResent, setCodeResent] = useState(false);

    const {
      control,
      handleSubmit,
      formState: {errors, touchedFields, isSubmitting},
      reset,
      setError,
    } = useForm<VerificationFormData>({
      resolver: zodResolver(verificationSchema),
      defaultValues: {
        verification: '',
      },
      mode: 'onBlur',
    });

    const onSubmit = async (values: VerificationFormData) => {
      await _onSubmit(values, {
        setError,
      });
    };

    const resendCode = async () => {
      setResending(true);

      // apiService can stall so set timeout here
      const resendTimeout = setTimeout(() => {
        setResending(false);
        setCodeResent(false);
      }, TIMEOUT);

      try {
        const result = await slowDown(_onResend());

        const success = result === true;
        setCodeResent(success);
      } catch (_e) {
        setCodeResent(false);
      } finally {
        clearTimeout(resendTimeout);
        setResending(false);
        reset(); // Reset form after resending
      }
    };

    // Wrapper to handle async function for Text onPress (which expects sync handler)
    const handleResendPress = () => {
      resendCode().catch(error => {
        // Log error but don't crash - already handled in resendCode
        console.error('Resend code failed:', error);
      });
    };

    const sentCodeText =
      type === 'email' ? "WE'VE SENT A CODE TO YOUR EMAIL" : "WE'VE SENT THE CODE";

    return (
      <KeyboardAwareScrollView
        style={{backgroundColor: 'white', flex: 1}}
        contentContainerStyle={{flex: 1}}
        keyboardShouldPersistTaps="always"
        enableOnAndroid
        extraScrollHeight={40}>
        <ContentView parentStyle={{flex: 1, paddingBottom: 16}} style={{flex: 1}}>
          <BackButton onPress={onBack} />
          <RewardsProgramImage variant="password" />
          <Text variant="heading1" style={{marginTop: 20}}>
            {codeResent ? 'CODE RE-SENT' : sentCodeText}
          </Text>

          {codeResent ? (
            <Text style={{marginBottom: 20}}>We have re-sent your code to your mobile.</Text>
          ) : null}

          <Text>
            Please verify your {type === 'phone' ? 'number' : 'email'} by entering the code sent to{' '}
            {type === 'phone' ? phoneNumber : emailAddress}.{' '}
            {type === 'phone' && (
              <Pressable onPress={() => onBack()}>
                <Text style={{color: '#0054A6'}}>Change number</Text>
              </Pressable>
            )}
          </Text>

          <Text
            variant="body2"
            style={[
              {marginTop: 20, marginBottom: 8},
              !!errors.verification && touchedFields.verification && {color: '#C81E1E'},
            ]}>
            Enter code:
          </Text>

          <Controller
            control={control}
            name="verification"
            render={({field: {onChange, onBlur, value}}) => (
              <Otp
                value={value}
                numInputs={6}
                onChangeText={onChange}
                onEnter={handleSubmit(onSubmit)}
                onBlur={onBlur}
                error={!!errors.verification && touchedFields.verification}
              />
            )}
          />

          <ValidationMessage>
            {touchedFields.verification && errors.verification ? errors.verification.message : ' '}
          </ValidationMessage>

          <View
            style={{
              backgroundColor: '#F9FAFB',
              borderRadius: 8,
              height: 52,
              justifyContent: 'center',
              paddingHorizontal: 12,
            }}>
            <Text variant="body2" style={{color: '#6B7280'}}>
              Didn&apos;t get a code?{' '}
              {resending ? (
                <Text variant="body2">Click to resend</Text>
              ) : (
                <Text onPress={handleResendPress} variant="body2" style={{color: '#1A56DB'}}>
                  Click to resend
                </Text>
              )}
            </Text>
          </View>

          <View style={{marginTop: 32}}>
            <Button
              title={'VERIFY'}
              onPress={handleSubmit(onSubmit)}
              disabled={isSubmitting}
              loading={isSubmitting}
            />
          </View>
        </ContentView>
      </KeyboardAwareScrollView>
    );
  },
);

export default PhoneOrEmailVerification;
